import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminItemsPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const ItemSkeleton = () => (
  <div className="AIP-skeleton">
    <div className="AIP-skeleton-image" />
    <div className="AIP-skeleton-body">
      <div className="AIP-skeleton-line AIP-skeleton-line--short" />
      <div className="AIP-skeleton-line AIP-skeleton-line--medium" />
      <div className="AIP-skeleton-line AIP-skeleton-line--long" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="AIP-stat-card">
    <div className={classNames('AIP-stat-icon', `AIP-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="AIP-stat-content">
      <div className="AIP-stat-value">{value}</div>
      <div className="AIP-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, showArchived }) => (
  <div className="AIP-empty-state">
    <i className="bi-box-seam AIP-empty-icon" />
    <h5 className="AIP-empty-title">
      {showArchived ? 'Aún no hay items archivados' : 'Aún no hay items'}
    </h5>
    <p className="AIP-empty-description">
      {showArchived
        ? 'No hay items archivados en este momento.'
        : 'Crea tu primer item para comenzar a gestionar tu inventario.'}
    </p>
    {!showArchived && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear item
      </Button>
    )}
  </div>
);

const AdminItemsPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos específicos de items
  const hasViewPanel = useMemo(() => hasPermission(user, PERMISSION_IDS.PANEL_ITEMS), [user]);
  const hasViewItems = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_ITEMS), [user]);
  const hasCreateItems = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_ITEMS), [user]);
  const hasArchiveItems = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_ITEMS), [user]);

  // Si no tiene permiso para ver el panel, redirigir
  useEffect(() => {
    if (user && !hasViewPanel && !hasViewItems) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de items.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewPanel, hasViewItems, navigate, notify]);

  const [stats, setStats] = useState({ 
    totalItems: 0, 
    activeItems: 0, 
    archivedItems: 0,
    totalStock: 0 
  });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingItems, setLoadingItems] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createItemName, setCreateItemName] = useState('');
  const [createItemCategory, setCreateItemCategory] = useState('');
  const [creatingItem, setCreatingItem] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/items/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats({
        totalItems: data.totalItems ?? 0,
        activeItems: data.activeItems ?? 0,
        archivedItems: data.archivedItems ?? 0,
        totalStock: data.totalStock ?? 0,
      });
    } catch (error) {
      logError('[AdminItemsPage] Error fetching stats:', error);
      notify({
        title: 'No pudimos obtener las estadísticas',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchCategories = async () => {
    if (!token) return;
    try {
      const response = await fetch(buildApiUrl('/api/categorias'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar categorías (${response.status})`);
      }
      const data = await response.json();
      setCategories(Array.isArray(data.categorias) ? data.categorias : []);
    } catch (error) {
      logError('[AdminItemsPage] Error fetching categories:', error);
      notify({
        title: 'Error al cargar categorías',
        message: error.message,
        variant: 'error',
      });
    }
  };

  const fetchItems = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingItems(true);
    try {
      const url = buildApiUrl(`/api/items?archived=${showArchived}`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar los items (${response.status})`);
      }
      const data = await response.json();
      const itemsList = Array.isArray(data.items) ? data.items : [];
      setItems(itemsList);
    } catch (error) {
      logError('[AdminItemsPage] Error fetching items:', error);
      notify({
        title: 'No pudimos cargar los items',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await Promise.all([fetchStats(), fetchItems(), fetchCategories()]);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showArchived]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStats(true), fetchItems(true), fetchCategories()]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'La lista de items y estadísticas se sincronizaron correctamente.',
      variant: 'success',
    });
  };

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const name = item.NombreItem || '';
      const sku = item.sku || '';
      return (
        name.toLowerCase().includes(term) ||
        sku.toLowerCase().includes(term) ||
        String(item.id).includes(term)
      );
    });
  }, [items, search]);

  const handleCreateItem = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateItemName('');
    setCreateItemCategory('');
  };

  const handleSubmitCreateItem = async (event) => {
    event.preventDefault();
    if (!createItemName.trim() || !createItemCategory) {
      notify({
        title: 'Campos requeridos',
        message: 'Por favor ingresa un nombre y selecciona una categoría.',
        variant: 'warning',
      });
      return;
    }

    setCreatingItem(true);
    try {
      const response = await fetch(buildApiUrl('/api/items'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          NombreItem: createItemName.trim(),
          IDCategoria: parseInt(createItemCategory)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al crear item (${response.status})`);
      }

      const data = await response.json();
      notify({
        title: 'Item creado',
        message: 'El item se creó exitosamente. Redirigiendo al detalle...',
        variant: 'success',
      });

      handleCloseCreateModal();
      await Promise.all([fetchStats(true), fetchItems(true)]);
      
      if (data.item?.id) {
        setTimeout(() => {
          navigate(`/admin/items/${data.item.id}`);
        }, 1000);
      }
    } catch (error) {
      notify({
        title: 'Error al crear item',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingItem(false);
    }
  };

  const handleCreateCategory = () => {
    setShowCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryName('');
    setCategoryDescription('');
  };

  const handleSubmitCreateCategory = async (event) => {
    event.preventDefault();
    if (!categoryName.trim()) {
      notify({
        title: 'Nombre requerido',
        message: 'Por favor ingresa un nombre para la categoría.',
        variant: 'warning',
      });
      return;
    }

    setCreatingCategory(true);
    try {
      const response = await fetch(buildApiUrl('/api/categorias'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          NombreCategoria: categoryName.trim(),
          descripcion: categoryDescription.trim() || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al crear categoría (${response.status})`);
      }

      notify({
        title: 'Categoría creada',
        message: 'La categoría se creó exitosamente.',
        variant: 'success',
      });

      handleCloseCategoryModal();
      await fetchCategories();
    } catch (error) {
      notify({
        title: 'Error al crear categoría',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleViewArchived = () => {
    setShowArchived(!showArchived);
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="items"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AIP-container">
          <div className="AIP-header">
            <div className="AIP-header-content">
              <h1 className="AIP-title">
                <i className="bi-box-seam-fill text-primary" />
                Gestión de Items
              </h1>
              <p className="AIP-subtitle">
                Administra tu inventario, controla el stock y gestiona tus productos.
              </p>
            </div>
            <div className="AIP-actions">
              {hasArchiveItems && (
              <Button
                variant={showArchived ? 'secondary' : 'outline-secondary'}
                onClick={handleViewArchived}
              >
                <i className="bi-archive me-2" />
                {showArchived ? 'Ver activos' : 'Ver archivados'}
              </Button>
              )}
              {hasCreateItems && (
              <Button variant="primary" onClick={handleCreateItem}>
                <i className="bi-plus-circle me-2" />
                Crear item
              </Button>
              )}
            </div>
          </div>

          <section className="AIP-stats-section">
            <div className="AIP-stats-grid">
              <StatsCard
                icon="bi-box-seam-fill"
                label="Total items"
                value={loadingStats ? '--' : stats.totalItems}
                variant="primary"
              />
              <StatsCard
                icon="bi-check-circle-fill"
                label="Items activos"
                value={loadingStats ? '--' : stats.activeItems}
                variant="success"
              />
              <StatsCard
                icon="bi-archive-fill"
                label="Items archivados"
                value={loadingStats ? '--' : stats.archivedItems}
                variant="muted"
              />
              <StatsCard
                icon="bi-stack"
                label="Stock total"
                value={loadingStats ? '--' : stats.totalStock}
                variant="info"
              />
            </div>
          </section>

          <section className="AIP-search-section">
            <div className="AIP-search-card">
              <div className="AIP-search-content">
                <Form.Control
                  type="search"
                  placeholder="Buscar por nombre, SKU o ID..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="AIP-search-input"
                />
                <div className="AIP-search-actions">
                  <Button
                    variant="outline-primary"
                    onClick={() => setSearch('')}
                    disabled={!search}
                    size="sm"
                  >
                    Limpiar
                  </Button>
                  <Button variant="primary" onClick={handleRefresh} disabled={isRefreshing} size="sm">
                    {isRefreshing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Actualizando
                      </>
                    ) : (
                      <>
                        <i className="bi-arrow-repeat me-2" />
                        Actualizar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="AIP-categories-section">
            <div className="AIP-categories-header" onClick={() => setExpandedCategories(!expandedCategories)}>
              <h2 className="AIP-categories-title">
                <i className={`bi-chevron-${expandedCategories ? 'down' : 'right'} me-2`} />
                Categorías
              </h2>
              {hasPermission(user, PERMISSION_IDS.CREAR_CATEGORIA) && (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateCategory();
                  }}
                >
                  <i className="bi-plus-circle me-2" />
                  Crear categoría
                </Button>
              )}
            </div>
            {expandedCategories && (
              <div className="AIP-categories-grid">
                {categories.length === 0 ? (
                  <p className="text-muted">No hay categorías creadas.</p>
                ) : (
                  categories.map((category) => (
                    <div key={category.id} className="AIP-category-card">
                      <h5>{category.NombreCategoria}</h5>
                      {category.descripcion && <p className="text-muted small">{category.descripcion}</p>}
                    </div>
                  ))
                )}
              </div>
            )}
          </section>

          <section className="AIP-items-section">
            {loadingItems ? (
              <div className="AIP-items-grid">
                {Array.from({ length: 6 }).map((_, index) => (
                  <ItemSkeleton key={index} />
                ))}
              </div>
            ) : filteredItems.length === 0 ? (
              <EmptyState onCreate={handleCreateItem} showArchived={showArchived} />
            ) : (
              <div className="AIP-items-grid">
                  {filteredItems.map((item) => {
                    const imageUrl = item.images && item.images.length > 0 
                      ? resolveImageUrl(item.images[0].image_url)
                      : resolveImageUrl(item.ImagenesItems);

                    return (
                      <div key={item.id} className="AIP-item-card">
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.NombreItem || 'Item'} className="AIP-item-image" />
                        ) : (
                          <div className="AIP-item-image-placeholder">
                            <i className="bi-box-seam" />
                          </div>
                        )}
                        <div className="AIP-item-body">
                          <div className="AIP-item-badges">
                            <Badge bg={item.is_active ? 'success' : 'secondary'}>
                              {item.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                            {item.archivado && <Badge bg="dark">Archivado</Badge>}
                            {item.Oferta && <Badge bg="info">En oferta</Badge>}
                          </div>
                          <h3 className="AIP-item-title">{item.NombreItem || 'Item sin nombre'}</h3>
                          <p className="AIP-item-sku">SKU: {item.sku || 'N/A'}</p>
                          <div className="AIP-item-stats">
                            <span className="AIP-item-stat-item">
                              <i className="bi-stack" />
                              Stock: {item.stockdisponible ?? 0}
                            </span>
                            {item.categoria && (
                              <span className="AIP-item-stat-item">
                                <i className="bi-tag" />
                                {item.categoria.NombreCategoria}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="primary"
                            className="AIP-item-action"
                            onClick={() => navigate(`/admin/items/${item.id}`)}
                          >
                            <i className="bi-box-arrow-in-right me-2" />
                            Ver detalle
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
            )}
          </section>
          </div>
        </main>
      </div>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-plus-circle me-2" />
            Crear Nuevo Item
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitCreateItem}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Item *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre del item"
                value={createItemName}
                onChange={(event) => setCreateItemName(event.target.value)}
                required
                autoFocus
                maxLength={100}
                disabled={creatingItem}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categoría *</Form.Label>
              <Form.Select
                value={createItemCategory}
                onChange={(event) => setCreateItemCategory(event.target.value)}
                required
                disabled={creatingItem}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.NombreCategoria}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCreateModal} disabled={creatingItem}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingItem || !createItemName.trim() || !createItemCategory}>
              {creatingItem ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear Item
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCategoryModal} onHide={handleCloseCategoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-tag me-2" />
            Crear Nueva Categoría
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitCreateCategory}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la Categoría *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre de la categoría"
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                required
                autoFocus
                maxLength={100}
                disabled={creatingCategory}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ingresa una descripción (opcional)"
                value={categoryDescription}
                onChange={(event) => setCategoryDescription(event.target.value)}
                maxLength={500}
                disabled={creatingCategory}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCategoryModal} disabled={creatingCategory}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingCategory || !categoryName.trim()}>
              {creatingCategory ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear Categoría
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

const AdminItemsPage = () => (
  <AdminNotificationsProvider>
    <AdminItemsPageInner />
  </AdminNotificationsProvider>
);

export default AdminItemsPage;

